import { commonEventAttributes, eventsTableSpecificAttributes } from "components/events/Constants";
import { ALPHABETICAL_SORT } from "components/common/PaginatedEntityTable/slicing/slicingConstants";
import { SECURITY_EVENT_STATUSES } from "security-app/hooks/api/securityEventsAPI.types";
import OwnerFilter from "security-app/components/SecurityEvents/Alerts/OwnerFilter";
import TacticTechniqueFilter from "security-app/components/SecurityEvents/Alerts/MitreCategories/TacticTechniqueFilter";
import EventDefinitionTypeFilter from "security-app/components/SecurityEvents/Alerts/EventDefinitionTypeFilter";
import RiskScoreFilter from "security-app/components/SecurityEvents/Alerts/RiskScoreFilter";
import AssociatedInvestigationsFilter from "security-app/components/SecurityEvents/Alerts/AssociatedInvestigationsFilter";
import AssociatedAssetsFilter from "security-app/components/SecurityEvents/Alerts/AssociatedAssetsFilter";
import usePermissions from "hooks/usePermissions";
import useLayoutVariant from "components/common/PaginatedEntityTable/hooks/useLayoutVariant";
export const SECURITY_EVENTS_ENTITY_TABLE_ID = "security_events";
export const RISK_SCORE_RANGE = { min: 0, max: 100 };
export const ALERTS_LAYOUTS_VARIANTS = {
  INVESTIGATIONS: "investigations",
  ASSETS: "assets",
  SYSTEM_ALERTS: "system_alerts",
  ALERTS: "alerts"
};
const SLICING_SORT_OPTIONS = {
  riskScore: {
    title: "Risk Score",
    value: "risk_score"
  },
  priority: {
    title: "Priority",
    value: "priority"
  }
};
export const securityEventDetailsAttributes = [
  ...commonEventAttributes,
  {
    id: "status",
    title: "Status",
    type: "STRING",
    filterable: true,
    sliceable: true,
    filter_options: Object.entries(SECURITY_EVENT_STATUSES).map(([value, title]) => ({ value, title })),
    slice_sort_default: { mode: ALPHABETICAL_SORT, direction: "desc" }
  },
  {
    id: "scores",
    title: "Risk Score",
    type: "STRING",
    sortable: true,
    filterable: true,
    sliceable: true,
    filter_component: RiskScoreFilter,
    slice_sort_default: { mode: ALPHABETICAL_SORT, direction: "desc" }
  },
  {
    id: "event_id",
    title: "ID",
    type: "STRING",
    sortable: true
  },
  {
    id: "associated_assets",
    title: "Associated Assets",
    type: "STRING",
    filterable: true,
    sliceable: true,
    slice_sort_options: [SLICING_SORT_OPTIONS.riskScore, SLICING_SORT_OPTIONS.priority],
    filter_component: AssociatedAssetsFilter,
    permissions: ["asset:read"],
    slice_sort_default: { mode: "risk_score", direction: "desc" }
  },
  {
    id: "associated_investigations",
    title: "Associated Investigations",
    type: "STRING",
    filterable: true,
    sliceable: true,
    slice_sort_options: [SLICING_SORT_OPTIONS.priority],
    sortable: true,
    filter_component: AssociatedInvestigationsFilter,
    permissions: ["investigations:read"],
    slice_sort_default: { mode: "priority", direction: "desc" }
  },
  {
    id: "fields",
    title: "Additional Fields",
    sortable: false
  }
];
export const categoryKey = "category";
export const attributes = [
  ...securityEventDetailsAttributes.filter((type) => type.id !== "event_definition_type"),
  // Drop OSS's plain tactics_techniques column — enterprise's categoryKey column renders both shapes.
  ...eventsTableSpecificAttributes.filter((attr) => attr.id !== "tactics_techniques").map((attr) => attr.id === "tags" ? { ...attr, searchable: true } : attr),
  {
    id: "owner",
    title: "Owner",
    type: "STRING",
    filterable: true,
    sliceable: true,
    filter_component: OwnerFilter
  },
  {
    id: "event_definition_type",
    title: "Event Definition Type",
    type: "STRING",
    filterable: true,
    filter_component: EventDefinitionTypeFilter
  },
  {
    id: categoryKey,
    title: "Tactics/Techniques",
    type: "STRING",
    filterable: true,
    filter_component: TacticTechniqueFilter
  }
];
const variantsSlicing = {
  [ALERTS_LAYOUTS_VARIANTS.INVESTIGATIONS]: {
    sliceColumn: "associated_investigations",
    sortBy: "priority",
    order: "desc",
    readOnly: true
  },
  [ALERTS_LAYOUTS_VARIANTS.ASSETS]: {
    sliceColumn: "associated_assets",
    sortBy: "risk_score",
    order: "desc",
    readOnly: true
  },
  [ALERTS_LAYOUTS_VARIANTS.SYSTEM_ALERTS]: {
    sliceColumn: null,
    sortBy: null,
    order: null,
    readOnly: true
  },
  [ALERTS_LAYOUTS_VARIANTS.ALERTS]: null
};
const variantsSort = {
  [ALERTS_LAYOUTS_VARIANTS.INVESTIGATIONS]: {
    attributeId: "scores",
    direction: "desc"
  },
  [ALERTS_LAYOUTS_VARIANTS.ASSETS]: {
    attributeId: "scores",
    direction: "desc"
  },
  [ALERTS_LAYOUTS_VARIANTS.SYSTEM_ALERTS]: {
    attributeId: "timestamp",
    direction: "desc"
  },
  [ALERTS_LAYOUTS_VARIANTS.ALERTS]: {
    attributeId: "timestamp",
    direction: "desc"
  }
};
export const useSecurityEventsTableElements = () => {
  var _a;
  const { isPermitted } = usePermissions();
  const { activeLayoutVariant } = useLayoutVariant();
  const canReadAssets = isPermitted("asset:read");
  const defaultDisplayedAttributes = [
    "message",
    "scores",
    categoryKey,
    "owner",
    "alert",
    "status",
    "timestamp",
    "associated_investigations",
    "associated_assets",
    "tags"
  ];
  const defaultColumnOrder = [
    "message",
    "scores",
    categoryKey,
    "owner",
    "alert",
    "status",
    "priority",
    "timestamp",
    "key",
    "event_definition_id",
    "event_definition_type",
    "group_by_fields",
    "remediation_steps",
    "event_id",
    "associated_investigations",
    "associated_assets",
    "tags"
  ];
  if (!canReadAssets) {
    const index = defaultDisplayedAttributes.indexOf("associated_assets");
    if (index > -1) defaultDisplayedAttributes.splice(index, 1);
    const columnIndex = defaultColumnOrder.indexOf("associated_assets");
    if (columnIndex > -1) defaultColumnOrder.splice(columnIndex, 1);
  }
  return {
    defaultLayout: {
      entityTableId: SECURITY_EVENTS_ENTITY_TABLE_ID,
      layoutVariant: activeLayoutVariant,
      defaultPageSize: 20,
      defaultSort: (_a = variantsSort == null ? void 0 : variantsSort[activeLayoutVariant]) != null ? _a : { attributeId: "timestamp", direction: "desc" },
      defaultSlicing: variantsSlicing == null ? void 0 : variantsSlicing[activeLayoutVariant],
      defaultDisplayedAttributes,
      defaultColumnOrder
    }
  };
};
